import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import TrafficPanel from '../components/TrafficPanel'

interface SessionRow {
  id: string
  created_at: number
  last_seen_at: number
  expires_at: number
  user_agent: string | null
}

/**
 * Account settings — and Module 8's most convincing demo.
 *
 * The session list is real. "Sign out everywhere" genuinely revokes every row,
 * which is the operation a stateless JWT cannot perform. Do it in one browser
 * tab and watch a second tab get logged out on its next request.
 */
export default function Account() {
  const { user, logout } = useAuth()
  const [sessions, setSessions] = useState<SessionRow[]>([])
  const [busy, setBusy] = useState(false)

  async function load() {
    const res = await api<{ sessions: SessionRow[] }>('/auth/sessions')
    setSessions(res.sessions)
  }

  useEffect(() => {
    void load().catch(() => {})
  }, [])

  async function logoutEverywhere() {
    setBusy(true)
    try {
      await api('/auth/logout-all', { method: 'POST' })
      await logout()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="account">
      <h1>Account</h1>

      <dl className="facts">
        <dt>Email</dt>
        <dd>{user?.email}</dd>
        <dt>Role</dt>
        <dd>
          {user?.role}
          {user?.role === 'owner' && (
            <span className="dim"> — you registered first, so migration 0001 made you owner.</span>
          )}
        </dd>
      </dl>

      <section>
        <h2>Active sessions</h2>
        <p className="dim">
          One row per device where you are signed in. The session token itself is not shown — the
          database only stores its SHA-256 hash, and the raw value exists solely in your cookie.
        </p>

        <ul className="session-list">
          {sessions.map((s) => (
            <li key={s.id}>
              <div>
                <strong>{describeAgent(s.user_agent)}</strong>
                <span className="dim">
                  {' '}
                  last seen {new Date(s.last_seen_at).toLocaleString()}
                </span>
              </div>
              <div className="dim">
                started {new Date(s.created_at).toLocaleDateString()} · expires{' '}
                {new Date(s.expires_at).toLocaleDateString()}
              </div>
            </li>
          ))}
        </ul>

        <button type="button" className="button" onClick={() => void logoutEverywhere()} disabled={busy}>
          {busy ? 'Revoking…' : 'Sign out everywhere'}
        </button>
      </section>

      <TrafficPanel title="The requests this page made" />
    </div>
  )
}

function describeAgent(ua: string | null): string {
  if (!ua) return 'Unknown device'
  if (/edg\//i.test(ua)) return 'Edge'
  if (/chrome\//i.test(ua)) return 'Chrome'
  if (/firefox\//i.test(ua)) return 'Firefox'
  if (/safari\//i.test(ua)) return 'Safari'
  if (/curl/i.test(ua)) return 'curl'
  return 'Browser'
}
